import { Divider } from "@aws-amplify/ui-react";
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import { useUpdate } from "@refinedev/core";
import { GrantPurpose } from "interfaces/grant-requests";
import { useState, useEffect } from "react";

interface ModalProps {
  open: boolean;
  refetch: any;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  id: number;
  toInform: string | undefined;
  grantPurpose: GrantPurpose;
}

export const UpdateGrantRequestInfo: React.FC<ModalProps> = ({
  setOpen,
  refetch,
  open,
  id,
  toInform,
  grantPurpose,
}) => {
  const { mutate, isLoading } = useUpdate();

  const [toInformValue, setToInformValue] = useState("");
  const [grantPurposeType, setGrantPurposeType] = useState<
    "SPECIFIC" | "UNRESTRICTED"
  >("UNRESTRICTED");
  const [grantPurposeNote, setGrantPurposeNote] = useState("");
  const [grantPurposeError, setGrantPurposeError] = useState(false);

  // Initialize form values when modal opens
  useEffect(() => {
    if (open) {
      setToInformValue(toInform || "");
      setGrantPurposeType(grantPurpose?.type || "UNRESTRICTED");
      setGrantPurposeNote(grantPurpose?.note || "");
    }
  }, [open, toInform, grantPurpose]);

  const handleClose = () => {
    setOpen(false);
    setToInformValue("");
    setGrantPurposeType("UNRESTRICTED");
    setGrantPurposeNote("");
    setGrantPurposeError(false);
  };

  const handleSubmit = () => {
    // Validate grant purpose note if type is SPECIFIC
    if (grantPurposeType === "SPECIFIC" && !grantPurposeNote.trim()) {
      setGrantPurposeError(true);
      return;
    }

    const updateValues: any = {
      toInform: toInformValue,
      grantPurpose: {
        type: grantPurposeType,
        ...(grantPurposeType === "SPECIFIC" && {
          note: grantPurposeNote.trim(),
        }),
      },
    };

    mutate(
      {
        resource: `update/info/${id}`,
        values: updateValues,
        id: "",
      },
      {
        onSuccess: () => {
          handleClose();
          refetch();
        },
      }
    );
  };

  return (
    <>
      <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
        <DialogTitle>Update Contribution Request Info</DialogTitle>
        <Divider />
        <DialogContent>
          <TextField
            margin="normal"
            fullWidth
            style={{ marginTop: 10 }}
            InputLabelProps={{ shrink: true }}
            value={toInformValue}
            onChange={(e) => setToInformValue(e.target.value)}
            type="text"
            name="toInform"
            label="To Inform (Optional)"
            multiline
            rows={3}
          />

          <FormControl
            fullWidth
            margin="normal"
            style={{ marginTop: 20 }}
            required
          >
            <InputLabel>Grant Purpose Type </InputLabel>
            <Select
              value={grantPurposeType}
              onChange={(e) => {
                setGrantPurposeType(
                  e.target.value as "SPECIFIC" | "UNRESTRICTED"
                );
                setGrantPurposeError(false);
              }}
              label="Grant Purpose Type *"
            >
              <MenuItem value="UNRESTRICTED">Unrestricted</MenuItem>
              <MenuItem value="SPECIFIC">Specific</MenuItem>
            </Select>
          </FormControl>

          {grantPurposeType === "SPECIFIC" && (
            <TextField
              error={grantPurposeError}
              margin="normal"
              fullWidth
              style={{ marginTop: 10 }}
              InputLabelProps={{ shrink: true }}
              value={grantPurposeNote}
              onChange={(e) => {
                setGrantPurposeNote(e.target.value);
                setGrantPurposeError(false);
              }}
              type="text"
              name="grantPurposeNote"
              label="Grant Purpose Note "
              multiline
              rows={2}
              helperText={
                grantPurposeError
                  ? "Note is required for specific grant purpose"
                  : "Provide details about the specific purpose"
              }
              required
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button
            disabled={
              isLoading ||
              (grantPurposeType === "SPECIFIC" && !grantPurposeNote.trim())
            }
            startIcon={
              isLoading && <CircularProgress size={20} color="inherit" />
            }
            onClick={handleSubmit}
          >
            Confirm
          </Button>
          <Button onClick={handleClose}>Cancel</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
